import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { useGetAllVideoInsights } from "@/api/studio/insights/queries";
import { useGetVideoBenchmark } from "@/api/studio/library/queries";
import { getVideoThumbnailUrl } from "@/api/renderer/api";
import { NavButton } from "@/components/ui/navigation";
import { RendererImage } from "@/components/ui/renderer-image";
import { ReportsLayout } from "@/components/layouts";
import { VideoAttentionGraph } from "@/features/reports/components/in-depth/video-attention-graph";
import { VideoInDepthSkeleton } from "@/features/reports/components/in-depth/video-in-depth-skeleton";
import { VideoMetricsDetails } from "@/features/reports/components/in-depth/video-metrics-details";
import { VideoPlayer } from "@/features/reports/components/in-depth/video-player";
import { VideoSceneDetail } from "@/features/reports/components/in-depth/video-scene-detail";
import { VideoScenesStrip } from "@/features/reports/components/in-depth/video-scenes-strip";
import { useSegment } from "@/hooks/use-segment";
import { useGlobalStore } from "@/stores/global-store";
import { cn } from "@/utils/classes";

/* Prop Types */
interface ReportsVideoInDepthRouteProps {
  projectId: string;
  studyId: string;
  videoKey: string;
}

/**
 * ReportsVideoInDepthRoute component
 *
 * Provides the route to drill into a specific video's Attention/Hook/Visual ease/Hold
 * scores, with a rail to switch between the other videos in the study.
 */
export const ReportsVideoInDepthRoute = ({ projectId, studyId, videoKey }: ReportsVideoInDepthRouteProps) => {
  /* Queries */
  const { data: allVideos, isLoading } = useGetAllVideoInsights({ projectId, studyId });
  const { data: benchmark } = useGetVideoBenchmark({ projectId, studyId });

  /* State */
  const [selectedSceneKey, setSelectedSceneKey] = useState<string | null>(null);

  /* Store */
  const theme = useGlobalStore((state) => state.theme);

  /* Hooks */
  const { segmentPageEvent } = useSegment();
  const [, setLocation] = useLocation();

  // Find the current video based on the videoKey prop
  const currentVideo = allVideos?.find((video) => video.key === videoKey);

  /* Handlers */
  const handleVideoClick = (key: string) => {
    // Navigate to the selected video
    setLocation(`/projects/${projectId}/studies/${studyId}/video/reports/in-depth/${key}`);
  };

  useEffect(() => {
    // segment page event when a user visits the video report in depth page
    if (theme)
      segmentPageEvent("Video Report", {
        title: "Video Report | Dragonfly AI Studio",
        theme: theme,
      });
  }, [theme]);

  useEffect(() => {
    // Clear the selected scene when switching to a different video
    setSelectedSceneKey(null);
  }, [videoKey]);

  /* Render */
  return (
    <ReportsLayout projectId={projectId} studyId={studyId} hideSidebar scrollPage>
      {/* Skeleton Loader */}
      {isLoading && <VideoInDepthSkeleton />}

      {allVideos && !isLoading && currentVideo && (
        <>
          {/* Header */}
          <div className="flex flex-row items-center justify-end gap-2">
            <div className="flex flex-row items-center mr-auto">
              <NavButton navigationUrl={`/projects/${projectId}/studies/${studyId}/video/reports/overview`} />
              <h2 className="font-semibold ml-2">{currentVideo.assetName}</h2>
            </div>
          </div>

          {/* Thumbnails */}
          <div className="grow flex flex-row gap-4 min-w-0">
            <div className="flex flex-col gap-4">
              {allVideos.map((video) => (
                <button
                  key={video.assetId}
                  type="button"
                  onClick={() => handleVideoClick(video.key)}
                  className={cn(
                    "shrink-0 size-[54px] rounded-lg overflow-hidden transition-opacity cursor-pointer",
                    video.key === videoKey ? "opacity-100 shadow-lg" : "opacity-40 hover:opacity-100",
                  )}
                >
                  <RendererImage src={getVideoThumbnailUrl({ projectId, studyId, assetKey: video.key })} alt={video.assetName} className="size-full" />
                </button>
              ))}
            </div>

            {/* Video Player + Attention Graph + Scenes */}
            <div className="flex-1 min-w-0 flex flex-col gap-4">
              <VideoPlayer projectId={projectId} studyId={studyId} videoKey={videoKey} scenes={currentVideo.scenes} selectedSceneKey={selectedSceneKey} />
              <VideoAttentionGraph key={videoKey} scenes={currentVideo.scenes} selectedSceneKey={selectedSceneKey} onSelectScene={setSelectedSceneKey} />
              <VideoScenesStrip
                projectId={projectId}
                studyId={studyId}
                videoKey={videoKey}
                scenes={currentVideo.scenes}
                selectedSceneKey={selectedSceneKey}
                onSelectScene={setSelectedSceneKey}
              />
              <VideoSceneDetail
                projectId={projectId}
                studyId={studyId}
                videoKey={videoKey}
                scenes={currentVideo.scenes}
                selectedSceneKey={selectedSceneKey}
                onSelectScene={setSelectedSceneKey}
              />
            </div>

            {/* Metrics Panel */}
            <VideoMetricsDetails currentVideo={currentVideo} benchmark={benchmark} />
          </div>
        </>
      )}
    </ReportsLayout>
  );
};
